import {Component} from 'react';
import PropTypes from 'prop-types';
import {Button, Container, Icon, Menu} from 'semantic-ui-react';
import {push} from 'react-router-redux';
import * as Routes from '../Routes';
import connected from '../connected';
import actionCreators from '../actionCreators/index';
import {endsWith} from 'lodash';
import AnalyticsGateway from '../lib/AnalyticsGateway';
import ShareMenu from './ShareMenu';
import './Footer.scss';

export class Footer extends Component {

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    featureFlags: PropTypes.shape({shareableLink: PropTypes.bool}),
    location: PropTypes.object.isRequired,
    match: PropTypes.object,
    propertyDetail: PropTypes.object.isRequired,
    reportType: PropTypes.shape({value: PropTypes.string.isRequired}).isRequired,
    reportUrl: PropTypes.string,
    finalise: PropTypes.shape({error: PropTypes.any}),
  };

  static defaultProps = {
    featureFlags: {},
    finalise: {},
  };

  isPdfAvailable = () => {
    const {reportUrl, finalise} = this.props;
    return !!reportUrl && reportUrl !== 'null' && !(finalise && finalise.error);
  };

  links = () => {
    return {
      'Comparable Rentals': {
        back: {
          link: () => '/properties/search',
          text: () => 'Report History',
        },
        primary: {
          link: (propertyId, reportType) => `/properties/${propertyId}/${reportType}/customise`,
          text: () => 'Next',
        },
      },
      'Comparable Sales': {
        back: {
          link: () => '/properties/search',
          text: () => 'Report History',
        },
        primary: {
          link: (propertyId, reportType) => {
            return reportType === 'soi'
              ? `/properties/${propertyId}/${reportType}/finalise`
              : `/properties/${propertyId}/${reportType}/comparable_listings`;
          },
          text: () => 'Next',
        },
      },
      'Comparable Listings': {
        back: {
          link: (propertyId, reportType) => `/properties/${propertyId}/${reportType}/comparable_sales`,
          text: () => 'Back',
        },
        primary: {
          link: (propertyId, reportType) => `/properties/${propertyId}/${reportType}/customise`,
          text: () => 'Next',
        },
      },
      'Summary': {
        back: {
          link: (propertyId, reportType) => {
            return (reportType === 'rental')
              ? `/properties/${propertyId}/${reportType}/comparable_rentals`
              : `/properties/${propertyId}/${reportType}/comparable_listings`;
          },
          text: () => 'Back',
        },
        primary: {
          link: (propertyId, reportType) => `/properties/${propertyId}/${reportType}/finalise`,
          text: () => 'Next',
        },
      },
      'Preview Report': {
        back: {
          link: (propertyId, reportType) => {
            return reportType === 'soi'
              ? `/properties/${propertyId}/${reportType}/comparable_sales`
              : `/properties/${propertyId}/${reportType}/customise`;
          },
          text: () => 'Back',
        },
        secondary: {
          attributes: (props) => {
            const filename = props.reportUrl && props.reportUrl.substring(props.reportUrl.lastIndexOf('/') + 1);
            const href = !this.isPdfAvailable() ? null :
              `/api/reports/download?contentDisposition=attachment;filename=${encodeURIComponent(filename)}&url=${encodeURIComponent(props.reportUrl)}`;
            return ({
              as: 'a',
              className: 'pdf-download-link',
              download: true,
              href,
              rel: 'noopener noreferrer',
              target: '_blank',
            });
          },
          eventName: 'Download Report',
          icon: 'download',
          link: () => null,
          text: () => 'Download PDF',
        },
        primary: {
          attributes: (props) => ({
            as: 'a',
            className: 'pdf-view-link',
            href: this.isPdfAvailable() ? props.reportUrl : null,
            rel: 'noopener noreferrer',
            target: '_blank',
          }),
          eventName: 'Open PDF Report',
          icon: 'file pdf outline',
          link: () => null,
          text: () => 'Open PDF',
        },
      },
    };
  };

  navigateTo = (uri, eventName) => {
    return (event) => {
      const {dispatch, location: {pathname}, propertyDetail: {propertyId}, reportType: {value: reportTypeValue}} = this.props;
      if (uri) {
        if (endsWith(uri, '/finalise')) {
          dispatch(actionCreators.navigateToFinalise(propertyId));
        } else {
          dispatch(push(uri));
        }
      }

      const {pageContext} = Routes.locationInfo(pathname);
      let properties = [
        {label: 'Page', value: pageContext},
        {label: 'Report Type', value: reportTypeValue},
        {label: 'Property ID', value: propertyId},
      ];

      if (eventName === 'Download Report') {
        properties[properties.length] = {label: 'Report Format', value: 'PDF'};
      }

      AnalyticsGateway.sendEvent({
        eventName,
        properties,
      });
      if (eventName !== 'Download Report' && eventName !== 'Open PDF Report') {
        event.preventDefault();
      }
    };
  };

  backMenu = () => {
    const {location: {pathname}, propertyDetail: {propertyId}, reportType: {value: reportTypeValue}} = this.props;
    const locationInfo = Routes.locationInfo(pathname);

    const backLink = this.links()[locationInfo.pageContext].back.link(propertyId, reportTypeValue);
    const backText = this.links()[locationInfo.pageContext].back.text(reportTypeValue);

    return (
      <Menu.Item>
        <Button
          className="back-step"
          icon
          labelPosition="left"
          onClick={this.navigateTo(backLink, 'Click Previous Step')}
          style={{backgroundColor: '#4c707e', color: 'white'}}
        >
          <Icon name="left arrow"/>
          {backText}
        </Button>
      </Menu.Item>
    );
  };

  secondaryRightMenu = () => {
    const {location: {pathname}, propertyDetail: {propertyId}, reportType: {value: reportTypeValue}} = this.props;
    const locationInfo = Routes.locationInfo(pathname);

    const secondaryData = this.links()[locationInfo.pageContext].secondary;
    if (secondaryData) {
      const secondaryAttributesFn = secondaryData.attributes;
      const secondaryAttributes = secondaryAttributesFn ? secondaryAttributesFn(this.props) : {};

      const secondaryLink = secondaryData.link(propertyId, reportTypeValue);
      const secondaryText = secondaryData.text(reportTypeValue);
      const secondaryIcon = secondaryData.icon;
      const secondaryEventName = secondaryData.eventName;

      return (
        <Menu.Item position="right" className="download-report-menu">
          <Button
            className="next-step"
            disabled={!this.isPdfAvailable() && secondaryText === 'Download PDF'}
            secondary
            icon
            labelPosition="right"
            onClick={this.navigateTo(secondaryLink, secondaryEventName)}
            {...secondaryAttributes}
          >
            <Icon name={secondaryIcon}/>
            {secondaryText}
          </Button>
        </Menu.Item>
      );
    }
    return null;
  };

  primaryRightMenu = (hasSecondaryMenu) => {
    const {
      location: {pathname},
      propertyDetail: {propertyId},
      reportType: {value: reportTypeValue},
    } = this.props;
    const locationInfo = Routes.locationInfo(pathname);

    const primaryData = this.links()[locationInfo.pageContext].primary;
    const primaryAttributesFn = primaryData.attributes;
    const primaryAttributes = primaryAttributesFn ? primaryAttributesFn(this.props) : {};

    const primaryLink = primaryData.link(propertyId, reportTypeValue);
    const primaryText = primaryData.text(reportTypeValue);
    const primaryIcon = primaryData.icon || 'right arrow';
    const primaryEventName = primaryData.eventName || 'Click Next Step';

    return (
      <Menu.Item position={hasSecondaryMenu ? null : 'right'}>
        <Button
          className="next-step"
          disabled={!this.isPdfAvailable() && primaryText === 'Open PDF'}
          primary
          icon
          labelPosition="right"
          onClick={this.navigateTo(primaryLink, primaryEventName)}
          {...primaryAttributes}
        >
          <Icon name={primaryIcon}/>
          {primaryText}
        </Button>
      </Menu.Item>
    );
  };

  render() {
    const {featureFlags, location, propertyDetail, reportType, reportUrl} = this.props;
    const backMenu = this.backMenu();
    const secondaryMenu = this.secondaryRightMenu();
    const primaryMenu = this.primaryRightMenu(secondaryMenu);
    const shareMenu = featureFlags.shareableLink ? (
      <ShareMenu
        location={location}
        propertyDetail={propertyDetail}
        reportType={reportType}
        reportUrl={this.isPdfAvailable() ? reportUrl : null}
        featureFlags={featureFlags}
      />
    ) : null;

    return (
      <div className="footer">
        <Menu compact fixed="bottom" size="large" color="grey" style={{backgroundColor: '#39424a'}}>
          <Container>
            {backMenu}
            {secondaryMenu}
            {primaryMenu}
            {shareMenu}
          </Container>
          <iframe src="about:blank" title="IE workaround" style={{
            border: 'none',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            position: 'absolute',
            zIndex: -1,
          }}
          />
        </Menu>
      </div>
    );
  }
}

export default connected(Footer);
